'use client';

import { getAccessToken } from '@/lib/supabase';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

type RequestInitOptions = {
  headers?: HeadersInit;
  // eslint-disable-next-line no-unused-vars
  onSuccess?: <T>(response: T) => void;
  // eslint-disable-next-line no-unused-vars
  onError?: (error: Error) => void;
  requireAuth?: boolean;
  timeout?: number;
  isFormData?: boolean;
  contentType?: string;
};

type RequestOptions = {
  headers?: HeadersInit;
  // eslint-disable-next-line no-unused-vars
  onSuccess?: <T>(response: T) => void;
  // eslint-disable-next-line no-unused-vars
  onError?: (error: Error) => void;
  requireAuth: boolean;
  timeout: number;
  isFormData: boolean;
  contentType?: string;
};

const defaultOptions: RequestOptions = {
  requireAuth: true,
  timeout: 30000,
  isFormData: false,
};

type ApiResponse<T> = T extends readonly unknown[]
  ? T
  : T extends { status: unknown }
    ? T & { headers?: Headers }
    : T & { headers?: Headers; status?: number };

export class ApiService {
  private static async getAuthToken(): Promise<string | null> {
    try {
      const session = await getAccessToken();
      if (!session?.access_token) {
        throw new Error('No session found');
      }

      return session.access_token;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error getting session');
    }
  }

  private static async handleRequest<T>(
    endpoint: string,
    options: RequestInit,
    requireAuth: boolean,
    timeout: number,
  ): Promise<ApiResponse<T>> {
    if (requireAuth) {
      try {
        const isServer = typeof window === 'undefined';
        const token = isServer ? null : await this.getAuthToken();
        if (!token) {
          throw new Error('No autenticado');
        }

        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        };
      } catch {
        throw new Error('Error getting token');
      }
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(endpoint, {
        ...options,
        headers: {
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error en la petición');
      }

      try {
        const data = await response.json();
        if (Array.isArray(data)) {
          return data as ApiResponse<T>;
        }
        return { ...data, status: response.status, headers: response.headers };
      } catch {
        return { status: response.status, headers: response.headers } as ApiResponse<T>;
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms`);
        }
        throw error;
      }
      throw new Error('Error fetching data');
    }
  }

  static async request<T, TData = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    endpoint: string,
    options: RequestOptions,
    data?: TData,
  ): Promise<ApiResponse<T>> {
    try {
      const requestOptions = {
        method,
        headers: {
          ...options.headers,
          ...(!options.isFormData && { 'Content-Type': 'application/json' }),
          ...(options.contentType && { 'Content-Type': options.contentType }),
        },
        ...(data && !options.isFormData ? { body: JSON.stringify(data) } : {}),
        ...(options.isFormData ? { body: data as FormData } : {}),
      };

      const response = await this.handleRequest<T>(
        `${API_URL}${endpoint}`,
        requestOptions,
        options.requireAuth,
        options.timeout || 30000,
      );

      options.onSuccess?.(response);
      return response;
    } catch (error) {
      const typedError = error instanceof Error ? error : new Error('Unknown error occurred');
      options.onError?.(typedError);
      throw typedError;
    }
  }

  static async get<T>(endpoint: string, options?: RequestInitOptions): Promise<ApiResponse<T>> {
    const requestOptions = {
      ...defaultOptions,
      ...options,
    };
    return this.request<T>('GET', endpoint, requestOptions);
  }

  static async post<T, TData = unknown>(
    endpoint: string,
    data: TData,
    options?: RequestInitOptions,
  ): Promise<ApiResponse<T>> {
    const requestOptions = {
      ...defaultOptions,
      ...options,
    };
    return this.request<T, TData>('POST', endpoint, requestOptions, data);
  }

  static async update<T, TData = unknown>(
    endpoint: string,
    data: TData,
    options?: RequestInitOptions,
  ): Promise<ApiResponse<T>> {
    const requestOptions = {
      ...defaultOptions,
      ...options,
    };
    return this.request<T, TData>('PUT', endpoint, requestOptions, data);
  }

  static async patch<T, TData = unknown>(
    endpoint: string,
    data: TData,
    options?: RequestInitOptions,
  ): Promise<ApiResponse<T>> {
    const requestOptions = {
      ...defaultOptions,
      ...options,
    };
    return this.request<T, TData>('PATCH', endpoint, requestOptions, data);
  }

  static async delete<T>(endpoint: string, options?: RequestInitOptions): Promise<ApiResponse<T>> {
    const requestOptions = {
      ...defaultOptions,
      ...options,
    };
    return this.request<T, null>('DELETE', endpoint, requestOptions);
  }

  static async upload<T>(
    endpoint: string,
    data: FormData,
    options?: RequestInitOptions,
  ): Promise<ApiResponse<T>> {
    const requestOptions = {
      ...defaultOptions,
      ...options,
    };
    return this.request<T, FormData>('PUT', endpoint, requestOptions, data);
  }

  static async downloadFile(
    endpoint: string,
    data?: unknown,
    options?: RequestInitOptions,
  ): Promise<Blob> {
    let requestOptions = {
      ...defaultOptions,
      ...options,
    };
    const { requireAuth, timeout } = requestOptions;

    if (requireAuth) {
      try {
        const isServer = typeof window === 'undefined';
        const token = isServer ? null : await this.getAuthToken();
        if (!token) {
          throw new Error('No autenticado');
        }

        requestOptions = {
          ...requestOptions,
          headers: {
            ...requestOptions?.headers,
            Authorization: `Bearer ${token}`,
          },
        };
      } catch {
        throw new Error('Error getting token');
      }
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: data ? 'POST' : 'GET',
        headers: {
          ...requestOptions?.headers,
          ...(data ? { 'Content-Type': 'application/json' } : {}),
        },
        signal: controller.signal,
        ...(data ? { body: JSON.stringify(data) } : {}),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error en la petición');
      }

      return await response.blob();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms`);
        }
        throw error;
      }
      throw new Error('Error downloading file');
    }
  }
}
